import * as React from 'react';
import styled from 'styled-components';

import Modal from '@joinforma/ui/components/Modal';
import Headline4 from '@joinforma/ui/components/Headline4';
import Headline6 from '@joinforma/ui/components/Headline6';
import Subtitle1 from '@joinforma/ui/components/Subheader1';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import { ReactComponent as FilledCheckSvg } from '@joinforma/ui/assets/icons/check-circle-filled.svg';
import {
  ButtonSize,
  ButtonVariant,
  SvgIconColor,
  SvgIconSize,
} from '@joinforma/ui/constants/properties';
import Button from '@joinforma/ui/components/Button';
import Body2 from '@joinforma/ui/components/Body2';

import hsaImg from 'assets/images/hsa-bulk-transfer.png';
import { HSA_BULK_TRANSFER_ELEMENTS } from 'constants/testSelectors';
import { HsaRoutes } from 'utils/routeGenerator';
import { useNavigate } from 'react-router-dom';

const ModalTitle = styled(Headline4)``;

const Illustration = styled.img.attrs({
  src: hsaImg,
  alt: 'Consolidate your HSA onto Forma!',
})`
  width: 100%;
`;

const Subtitle = styled(Headline6)`
  margin-bottom: 16px;
`;

const ModalContent = styled(Body2)<{ marginBottom?: string }>`
  display: block;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? marginBottom : '16px'};
`;

const CheckIcon = styled(SvgIcon).attrs({
  icon: FilledCheckSvg,
  fill: SvgIconColor.INTERACTIVE,
  size: SvgIconSize.MEDIUM,
})`
  margin-right: 13.2px;
`;

const BenefitsContent = styled(Subtitle1)`
  font-weight: 500;
`;

const PrimaryButton = styled(Button).attrs({
  size: ButtonSize.LARGE,
  variant: ButtonVariant.PRIMARY,
})`
  width: 100%;
  margin-bottom: 16px;
`;

const SecondaryButton = styled(Button).attrs({
  size: ButtonSize.LARGE,
  variant: ButtonVariant.SECONDARY,
})`
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const HSABenefit = styled.div<{ marginBottom?: string | null }>`
  display: flex;
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? marginBottom : '24px'};
`;

const benefits = [
  'Earn 3x more APY on your HSA cash balance than WealthCare',
  'Invest HSA your money for tax-free growth',
  'Use your Forma HSA Debit Card to spend directly from your HSA',
];

interface AlegeusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AlegeusHSABulkTransferModal = ({
  isOpen,
  onClose,
}: AlegeusModalProps) => {
  const navigate = useNavigate();

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      customTitle={
        <ModalTitle>
          Transfer your HSA to Forma to manage everything in one place with
          exclusive benefits!
        </ModalTitle>
      }
      customMaxWidth="680px"
      hideCloseButton
    >
      <Illustration />

      <Subtitle data-testid={HSA_BULK_TRANSFER_ELEMENTS.ALEGEUS_MODAL}>
        Consolidate all your HSA money into one place
      </Subtitle>

      <ModalContent>
        Starting in 2023 you can manage your HSA cash and investments directly
        from Forma! No other platforms or providers, you get everything in one
        place.
      </ModalContent>

      <ModalContent>
        Since you have a WealthCare HSA, we can automatically consolidate it for
        you. Plus you'll get extra benefits exclusive to Forma:
      </ModalContent>

      {benefits.map((b, idx, arr) => (
        <HSABenefit
          key={b}
          marginBottom={arr.length - 1 === idx ? '34px' : null}
        >
          <CheckIcon />
          <BenefitsContent>{b}</BenefitsContent>
        </HSABenefit>
      ))}

      <ButtonContainer>
        <PrimaryButton
          data-testid={HSA_BULK_TRANSFER_ELEMENTS.ALEGEUS_MODAL_CTA}
          onClick={() => {
            onClose();
            navigate(HsaRoutes.alegeusBulkTransfer());
          }}
        >
          Yes, transfer my HSA money
        </PrimaryButton>
        <SecondaryButton onClick={() => onClose()}>
          Remind me later
        </SecondaryButton>
      </ButtonContainer>
    </Modal>
  );
};

export default AlegeusHSABulkTransferModal;
